/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, { useMemo } from 'react'
import { Loader } from '@dataplace.ai/ui-components/atoms'
import { useTranslation } from 'react-i18next'
import { RadarChart } from '@placeme/components/molecules/Bus/RadarChart'
import { RootState } from 'apps/placeme/src/redux/store'
import { useSelector } from 'react-redux'
import { IChainBenchmarkData } from './@types/IChainBenchmark'
import SimilarAddresses from './components/SimilarAddresses'
import { Title, Wrapper } from './ChainBenchmark.styles'
import AnalysedVariables from './components/AnalysedVariables'
import ChainBenchmarkDescriptionComponent from './components/ChainBenchmarkDescriptionComponent'
import { Settings } from '../../Mcd/Settings'

function ChainBenchmarkResult({ data }: { data: IChainBenchmarkData }) {
  const { t } = useTranslation()
  const { value: { address } = {} } = useSelector((state: RootState) => state.location)

  const settingsList = [{
    label: t('generic.label'),
    value: data.value.settings?.label ?? t('generic.none'),
    visible: true,
  }]

  const similarLocations = useMemo(() => data?.value?.benchmark?.similarLocations?.map((location) => ({
    address: location.address,
    similarity: {
      total: 0,
      metric: {
        totalNumber1Km: location.influence.population.totalNumber1Km.value,
        '60Share1Km': location.influence.population['60Share1Km'].value,
        under18Share1Km: location.influence.population.under18Share1Km.value,
        earnings1Km: location.influence.population.earnings1Km.value,
        averageDailyTraffic250M: location.influence.traffic.averageDailyTraffic250M.value,
        morningTrafficShare250M: location.influence.traffic.morningTrafficShare250M.value,
        eveningTrafficShare250M: location.influence.traffic.eveningTrafficShare250M.value,
      },
    },
  })), [data])

  if (data?.loading || !data?.value) return <Loader />

  if (!data.value.benchmark.similarLocations.length) {
    return (
      <Wrapper>
        <Settings selectedSettings={settingsList} />
        <ChainBenchmarkDescriptionComponent
          firstDescription='placeme.chain_benchmark.no_results_description_1'
          secondDescription='placeme.chain_benchmark.no_results_description_2'
        />
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <Settings selectedSettings={settingsList} />
      <AnalysedVariables valuesData={data.value.benchmark.valuesData} />
      <SimilarAddresses similarLocations={data.value.benchmark.similarLocations} />
      <Title $borderTop>{t('placeme.potential_within_chain.bus_chart.title')}</Title>
      <RadarChart
        analysedAddress={address ?? ''}
        hideRadarValueRange
        similarLocations={similarLocations}
      />
    </Wrapper>
  )
}

export default ChainBenchmarkResult
